import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '@/api/client'
import type { MyCertItem, MyCertNeed, MyCerts as MyCertsData } from '@/api/types'
import { badgeClass, dayLabel, outcomeTone, stateTone } from '@/lib/certs'

/**
 * My certifications — the portal's first screen for technicians.
 *
 * Read-only. Says in plain words whether each card is good and until when,
 * and what the person still needs. Anything to fix goes through the office:
 * there is no self-entry until Phase C decides on photo uploads.
 *
 * Medical cards show whether they are in order and nothing else, even to the
 * person who holds them, until NW Linings decides otherwise (design §13).
 *
 * Light on purpose: one request, nothing else loaded, and a retry when there
 * is no signal rather than a dead screen.
 */

function statusLine(c: MyCertItem): string {
  const until = c.expires_date && !c.redacted ? dayLabel(c.expires_date) : null
  switch (c.cert_state) {
    case 'Valid':
      return until ? `Good until ${until}` : 'Good — does not expire'
    case 'Expiring':
      return until
        ? `Expires ${until}${c.days_to_expiry !== null ? ` — ${c.days_to_expiry} days left` : ''}. Ask the office about renewing.`
        : 'Expiring soon. Ask the office about renewing.'
    case 'Expired':
      return until ? `Expired ${until} — see the office` : 'Expired — see the office'
    case 'BlockedByPrereq':
      return "Not counting yet — something it depends on isn't in place. See the office."
    case 'Reevaluate':
      return 'Needs re-evaluation — see the office'
    case 'Pending':
      return 'Waiting for the office to check it'
    default:
      return ''
  }
}

function needLine(n: MyCertNeed): string | null {
  const why = n.scope_job_no && !n.scope_label.startsWith('Job ')
    ? `${n.scope_label}, including ${n.scope_job_no}`
    : n.scope_label
  switch (n.outcome) {
    case 'Missing':
      return `${n.type_name} — required (${why}). None on file: bring your card to the office.`
    case 'Expired':
      return `${n.type_name} — required (${why}), and yours has expired.`
    case 'Blocked':
      return `${n.type_name} — required (${why}), and yours isn't counting yet.`
    case 'InGracePeriod':
      return `${n.type_name} — required (${why}). You're new, so there's time, but get it done.`
    default:
      return null
  }
}

export function MyCerts() {
  const [data, setData] = useState<MyCertsData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await api.myCerts())
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 404
          ? "Certifications aren't switched on for you yet."
          : "Couldn't load your certifications. Check your signal and try again.",
      )
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (!data) {
    return (
      <div className="stack">
        {error ? (
          <div className="alert alert-warn">
            {error}
            {!error.includes('switched on') && (
              <button className="btn btn-secondary btn-block" style={{ marginTop: 12 }} onClick={() => void load()}>
                Try again
              </button>
            )}
          </div>
        ) : (
          <p className="muted">{loading ? 'Loading…' : ''}</p>
        )}
      </div>
    )
  }

  const needs = data.needs.map((n) => [n, needLine(n)] as const).filter(([, line]) => line !== null)

  return (
    <div className="stack">
      <div className="card">
        <h2 style={{ marginTop: 0, marginBottom: 4 }}>My certifications</h2>
        <p className="muted" style={{ margin: 0 }}>
          {data.display_name} · <span className="mono">{data.employee_no}</span> · as of {dayLabel(data.as_of)}
        </p>
      </div>

      {needs.length > 0 && (
        <div className="alert alert-error">
          <strong>Needs attention</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: '1.2rem' }}>
            {needs.map(([n, line]) => (
              <li key={n.cert_type_id} style={{ marginBottom: 6 }}>{line}</li>
            ))}
          </ul>
        </div>
      )}

      {data.items.length === 0 ? (
        <div className="card">
          <p style={{ margin: 0 }}>
            No certifications on file. If you hold any — OSHA, forklift, first aid — bring the card to the office.
          </p>
        </div>
      ) : (
        data.items.map((c) => (
          <div className="card" key={c.cert_type_id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
              <strong style={{ fontSize: '1.0625rem' }}>{c.type_name}</strong>
              {c.cert_state && (
                <span className={badgeClass(stateTone(c.cert_state))}>
                  {c.cert_state === 'Valid' ? 'Good' : c.cert_state === 'BlockedByPrereq' ? 'On hold' : c.cert_state === 'Reevaluate' ? 'Re-check' : c.cert_state}
                </span>
              )}
            </div>
            <p style={{ margin: '6px 0 0' }}>{statusLine(c)}</p>
            {c.redacted ? (
              <p className="muted" style={{ margin: '4px 0 0', fontSize: '0.875rem' }}>
                Medical — the office keeps the details.
              </p>
            ) : (
              (c.cert_number || c.provider) && (
                <p className="muted" style={{ margin: '4px 0 0', fontSize: '0.875rem' }}>
                  {c.cert_number && <span className="mono">{c.cert_number}</span>}
                  {c.cert_number && c.provider && ' · '}
                  {c.provider}
                </p>
              )
            )}
          </div>
        ))
      )}

      {data.needs.some((n) => outcomeTone(n.outcome) === 'ok') && (
        <p className="muted" style={{ fontSize: '0.875rem' }}>
          Required and covered:{' '}
          {data.needs.filter((n) => outcomeTone(n.outcome) === 'ok').map((n) => n.type_name).join(', ')}.
        </p>
      )}
      <p className="muted" style={{ fontSize: '0.875rem' }}>
        Something wrong or out of date? Tell the office — they keep these records.
      </p>
    </div>
  )
}
